import produce from 'immer';
import { create } from 'zustand';

import type { PersonalPageDto, ProjectNode, UserSpecificTemplate } from 'api/data/client-dataapi';
import type { AccessRightsMap } from 'state/grids/gridState';
import type { Template } from 'state/selectedTemplate/selectedTemplateStore';

export type SelectedProjectState = {
  projectId?: string;
  template?: Template;

  nodeAccessRightsByRoleMap?: AccessRightsMap;
  propertyAccessRightsByRoleMap?: AccessRightsMap;
  relationAccessRightsByRoleMap?: AccessRightsMap;

  personalPageItems: { [key: string]: number } | null;
  personalPageGeneratedOn: Date | undefined;
  personalPageLoadError: boolean;

  setPersonalPageItems: (items: PersonalPageDto | null) => void;
  setPersonalPageLoadError: (hasError: boolean) => void;

  setProjectWithTemplate: (projectNode: ProjectNode, template: UserSpecificTemplate) => void;
  clear: () => void;
};

export const SELECTED_PROJECT_STORE = create<SelectedProjectState>((set) => ({
  projectId: undefined,
  template: undefined,
  nodeAccessRightsByRoleMap: undefined,
  propertyAccessRightsByRoleMap: undefined,
  relationAccessRightsByRoleMap: undefined,
  personalPageItems: null,
  personalPageGeneratedOn: undefined,
  personalPageLoadError: false,
  setPersonalPageItems: (personalPageDto: PersonalPageDto | null) =>
    set((state) => {
      const newState = produce(state, (draft) => {
        if (personalPageDto !== null) {
          draft.personalPageItems = personalPageDto.numberOfItems;
          draft.personalPageGeneratedOn = new Date();
          draft.personalPageLoadError = false;
        } else {
          draft.personalPageItems = null;
          draft.personalPageGeneratedOn = undefined;
        }
      });

      return newState;
    }),
  setPersonalPageLoadError: (hasError: boolean) => set({ personalPageLoadError: hasError }),
  setProjectWithTemplate: (projectNode: ProjectNode, template: UserSpecificTemplate) =>
    set((state) => {
      const newState = produce(state, (draft) => {
        draft.projectId = projectNode.id;
        draft.template = template.template;
        draft.nodeAccessRightsByRoleMap = template.nodeAccessRightsByRoleMap;
        draft.propertyAccessRightsByRoleMap = template.propertyAccessRightsByRoleMap;
        draft.relationAccessRightsByRoleMap = template.relationAccessRightsByRoleMap;
        // Only on a real project switch: a same-project template refresh (SignalR ProjectUpdated)
        // must not null the counts, since nothing would refetch them.
        if (state.projectId !== projectNode.id) {
          draft.personalPageItems = null;
          draft.personalPageGeneratedOn = undefined;
          draft.personalPageLoadError = false;
        }
      });

      return newState;
    }),
  clear: () =>
    set((state) => {
      const newState = produce(state, (draft) => {
        draft.projectId = undefined;
        draft.template = undefined;
        draft.personalPageItems = null;
        draft.personalPageGeneratedOn = undefined;
        draft.personalPageLoadError = false;
      });

      return newState;
    })
}));
